import { z } from 'zod';
import {
  amountSchema,
  paymentMethodsAllSchema,
  paymentStatusCommonSchema,
  paymentTermsSchema,
} from '../../common';

import { orderBeneficiaryTypeSchema } from './order.beneficiary.model';
import { InvoiceSchema } from '../invoice';
import { TransactionSchema } from '../transaction';
import { priceSchema } from '../../validation';
/** who is going to pay for the order */
export const orderPaymentResponsibleSchema = z.enum([
  ...orderBeneficiaryTypeSchema.options,
  'non_payable_order', // this is when used as a SaaS
]);

export const orderInvoiceSchema = InvoiceSchema.model
  .pick({ _id: true, ref: true, title: true })
  .extend({
    /** the total amount for this order that will be paid by this invoice. */
    total: priceSchema,

    /** the total paid amount */
    paid: priceSchema,

    /** the total remaining amount */
    remaining: priceSchema,
  });
export type OrderInvoice = z.infer<typeof orderInvoiceSchema>;

export const orderPaymentTransactionSchema = TransactionSchema.model
  .pick({
    _id: true,
    ref: true,
    amount: true,
    currency: true,
    type: true,
    invoices: true,
  })
  .extend({
    /** the method of the payment. if empty, then transaction is just capture. */
    method: paymentMethodsAllSchema.optional(),
  });
export type OrderPaymentTransaction = z.infer<typeof orderPaymentTransactionSchema>;

export const orderPaymentSchema = z.object({
  /** payment status (paid / not_paid / etc) */
  status: paymentStatusCommonSchema,

  /** the transactions that are linked to the order. if empty, it means the order is not linked to any payment yet.
   *
   * This is an array because in some cases an order can have multiple transactions.
   *
   * For example, if the order is paid in installments, then there will be multiple transactions.
   *
   * Also, each transaction can have multiple invoices.
   *
   */
  transactions: z.array(orderPaymentTransactionSchema),
  /**
   * the invoices that are linked to the order.
   *
   * It's rare for one order to have multiple invoices. One of the reasons of why we might have more than one invoice for one order is advance payment.
   *
   * For example, the user has paid 1,000 SAR in advance (new invoice of 1,000 is created). And then a new order is created for 1,500 SAR.
   *
   * In this case, the order will be linked to 2 invoices (1,000 SAR advance payment invoice) and the other one will be created immediately for 500 SAR.
   * So this way, we can track how much was spent on every order by which invoice. This helps our customers to track their shipments financially much more accurate.
   *
   * **Furthermore**, every invoice is `accumulated` meaning, if one invoice has multiple transactions such as 100, 200 SAR. This invoices
   *  array will have one invoice with the total of 300 SAR.
   */
  invoices: z.array(orderInvoiceSchema),

  /** the payment terms agreed upon */
  terms: paymentTermsSchema,
});
export type OrderPayment = z.infer<typeof orderPaymentSchema>;
export const orderAmountAndPaymentSchema = z.object({
  /** the amount info to be charged such as total / subtotal / tax / etc */
  amount: amountSchema,
  /** payment info such as (method, status, id, etc) */
  payment: orderPaymentSchema,
});
export type OrderAmountAndPayment = z.infer<typeof orderAmountAndPaymentSchema>;

/**
 * Which pricing mechanism produced the price recorded on a contract leg /
 * offer target. **Origin audit metadata only** — set at leg create,
 * never updated post-create. For **final commercial state** (whether
 * the current load price honors a frozen route quote), see
 * `Order.contracts[i].priceBasis` and `getPriceFinality` /
 * `getDisplayPriceCategory` in `@hero/core`. Persist this so post-
 * mortem analytics can reconstruct "where did this price come from?" without
 * replaying the resolver.
 *
 * UI buckets for **origin** (via `getPriceCategory`):
 *
 * | Category    | Strategies                                              | Price > 0? | Set by                                                    |
 * | ----------- | ------------------------------------------------------- | ---------- | --------------------------------------------------------- |
 * | agreement   | `route_directed_pricing`                                | yes        | `PricingResolutionService` (directed Route match)          |
 * | everyone    | `route_seller_own_pricing`, `route_buyer_own_pricing`   | yes        | `PricingResolutionService` (own Route match)               |
 * | one_time    | `one_time_pricing`, `admin_custom_pricing`              | yes        | caller passed `route.price` on create / super-admin override |
 * | unresolved  | `unresolved_pricing`                                    | NO (=0)    | resolver fell through (no Route, no caller-supplied price) |
 *
 * Per-value semantics:
 *
 *   - `route_directed_pricing`        Resolver matched a directed Route
 *                                     (`isOwnRoute=false`) between the exact
 *                                     `(seller, buyer)` parties. The
 *                                     bilateral price the two of them agreed
 *                                     on for this lane. Persists the Route
 *                                     snapshot in `leg.route`.
 *
 *   - `route_seller_own_pricing`      Resolver matched the seller's own
 *                                     Route (`isOwnRoute=true`, buyer
 *                                     empty). The seller's published "rate
 *                                     card" — what they charge anyone who
 *                                     doesn't have a directed agreement.
 *                                     Persists the Route snapshot.
 *
 *   - `route_buyer_own_pricing`       Resolver matched the buyer's own
 *                                     Route (`isOwnRoute=true`, seller
 *                                     empty). The buyer's "default budget"
 *                                     — what they're willing to pay anyone
 *                                     for this lane. Persists the Route
 *                                     snapshot.
 *
 *   - `one_time_pricing`              Caller passed `route.price` on the
 *                                     create-order payload. Bypasses Route
 *                                     resolution entirely. Persists no
 *                                     `leg.route` snapshot (this price is a
 *                                     one-off, NOT promoted into the saved
 *                                     Route book unless the user opts into
 *                                     "save as Route"). Used by the reorder
 *                                     modal's smart-lock when the source
 *                                     order itself was `one_time` /
 *                                     positive `unresolved`.
 *
 *   - `admin_custom_pricing`          Super admin set the subtotal manually
 *                                     in the create payload's
 *                                     `beneficiaryAmount.itemsOriginalSubtotal`
 *                                     (see `OrderPriceService.withAdminPrices`).
 *                                     Conceptually a one-time override too,
 *                                     but tagged separately so audit logs
 *                                     can distinguish "admin patched this"
 *                                     from "user typed a price".
 *
 *   - `unresolved_pricing`            No Route matched and the caller did
 *                                     not supply a price. The leg is
 *                                     persisted with subtotal `0`, awaiting
 *                                     manual entry via the price-edit
 *                                     drawer. Replaces the old
 *                                     `distance_based_pricing` label which
 *                                     was a misnomer (the per-km rate
 *                                     table was a broker-era artifact and
 *                                     `calculateVehiclePriceBasedOnDistance`
 *                                     always returns 0).
 *
 * @deprecated values (declared for legacy-read compatibility; never written
 * by new code; scheduled for removal post-migration once production rows
 * are confirmed clean):
 *
 *   - `platform_route_pricing`        Broker-era "platform took a cut"
 *                                     pricing. Removed in Phase 7 (Hero is
 *                                     a network, not a broker — no platform
 *                                     entity in pricing flows). Backfilled
 *                                     to `route_directed_pricing` by the
 *                                     pricing-strategy-cleanup migration.
 *
 *   - `distance_based_pricing`        Misleading label — always paired with
 *                                     subtotal 0. Backfilled to
 *                                     `unresolved_pricing` by the cleanup
 *                                     migration. New code writes
 *                                     `unresolved_pricing` directly.
 *
 *   - `override_route_directed_pricing` Declared, never written by any
 *                                     code path. The intended semantic
 *                                     (offer counter-price overriding a
 *                                     directed Route) is now covered by
 *                                     `one_time_pricing`. Backfilled to
 *                                     `route_directed_pricing` defensively.
 */
export const orderPricingStrategySchema = z.enum([
  // ── current write targets ────────────────────────────────────────
  'route_directed_pricing',
  'route_seller_own_pricing',
  'route_buyer_own_pricing',
  'one_time_pricing',
  'admin_custom_pricing',
  'unresolved_pricing',

  // ── @deprecated, kept only for legacy-read tolerance ─────────────
  // ── (the pricing-strategy-cleanup migration backfills these) ─────
  'platform_route_pricing',
  'distance_based_pricing',
  'override_route_directed_pricing',
]);
export type OrderPricingStrategy = z.infer<typeof orderPricingStrategySchema>;

/** all payment info (carrier & customer) */
export const orderPaymentAllSchema = z.object({
  /** who is going to pay for the shipping costs. */
  responsibility: orderPaymentResponsibleSchema,
  /** From whom we will be collection the money. */
  collection: orderPaymentResponsibleSchema,

  /** carrier payment info */
  carrier: orderAmountAndPaymentSchema,

  /** customer payment info for the shipping costs.
   *
   * this will be the `user` of the order.
   **/
  beneficiary: orderAmountAndPaymentSchema,

  /** commission is the diff between carrier & customer.
   *
   * Therefore, mainly it's for the platform (Hero)
   */
  commission: orderAmountAndPaymentSchema,
  /**
   * Top-level pricing strategy is deprecated alongside the carrier/customer/
   * commission split below; per-leg strategy now lives in
   * `Order.contracts[i].pricingStrategy`. Kept here optional so legacy reads
   * compile during the staging cutover.
   */
  pricingStrategy: orderPricingStrategySchema.optional(),
});

export type OrderPaymentAll = z.infer<typeof orderPaymentAllSchema>;
